import { useCallback, useMemo, useState } from 'react';

import { useTranslation } from 'next-i18next/pages';

import { Button, DatePicker, Dropdown, MenuProps } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { capitalize } from 'lodash';

import { TranslationNamespace } from '@/i18n';

import { disabledDateFunc } from './utils/disabledDays';

import styles from './DateRangeDropdown.module.scss';

type RangeValue = [Dayjs, Dayjs];

interface DateRangeDropdownProps {
	value: RangeValue;
	fullWidth?: boolean;
	blackList?: ('year' | 'years')[];
	disabledDays?: number;
	maxDate?: Dayjs;
	onChange: (value: RangeValue) => void;
}

export const DateRangeDropdown = ({
	value,
	fullWidth = false,
	blackList = [],
	disabledDays,
	maxDate,
	onChange,
}: DateRangeDropdownProps) => {
	const { t } = useTranslation(TranslationNamespace.Shared);
	const [open, setOpen] = useState(false);
	const [showCustomRange, setShowCustomRange] = useState(false);

	const now = dayjs();

	const ranges = useMemo(() => {
		const months = Array.from({ length: now.month() }, (_, index) => {
			const month = now.startOf('year').add(index, 'month');

			return {
				key: `month-${month.format('YYYY-MM')}`,
				label: capitalize(month.format('MMMM')),
				value: [month.startOf('month'), month.endOf('month')] as RangeValue,
			};
		}).reverse();
		const years = blackList.includes('years')
			? []
			: Array.from({ length: 5 }, (_, index) => {
					const year = now.subtract(index + 1, 'year');

					return {
						key: `year-${year.year()}`,
						label: year.format('YYYY'),
						value: [year.startOf('year'), year.endOf('year')] as RangeValue,
					};
				});

		const currentYear = blackList.includes('year')
			? []
			: [
					{
						key: '365-days',
						label: t('date.365-days'),
						value: [now.subtract(365, 'day'), now] as RangeValue,
					},
				];

		const quickRanges = [
			{
				key: '30-days',
				label: t('date.30-days'),
				value: [now.subtract(30, 'day'), now] as RangeValue,
			},
			{
				key: '90-days',
				label: t('date.90-days'),
				value: [now.subtract(90, 'day'), now] as RangeValue,
			},
			...currentYear,
			{
				key: 'current-month',
				label: t('date.current-month'),
				value: [now.startOf('month'), now] as RangeValue,
			},
		];

		return { quickRanges, months, years };
	}, [blackList, t]);

	const handleRangeChange = (range: null | [Dayjs | null, Dayjs | null]) => {
		if (!range?.[0] || !range?.[1]) {
			return;
		}

		onChange([range[0], range[1]]);

		setShowCustomRange(false);
		setOpen(false);
	};

	const disabledDate = useCallback(() => {
		if (disabledDays) return disabledDateFunc(disabledDays);
		return undefined;
	}, [disabledDays]);

	const items: MenuProps['items'] = [
		...ranges.quickRanges.map(({ key, label, value: rangeValue }) => ({
			key,
			label,
			onClick: () => {
				onChange(rangeValue);
				setOpen(false);
			},
		})),
		{
			key: 'months',
			label: t('date.month'),
			popupClassName: styles.submenuPopup,
			children: ranges.months.map(({ key, label, value: rangeValue }) => ({
				key,
				label,
				onClick: () => {
					onChange(rangeValue);
					setOpen(false);
				},
			})),
		},
		...(ranges.years.length
			? [
					{
						key: 'years',
						label: t('date.year'),
						popupClassName: styles.submenuPopup,
						children: ranges.years.map(({ key, label, value: rangeValue }) => ({
							key,
							label,
							onClick: () => {
								onChange(rangeValue);
								setOpen(false);
							},
						})),
					},
				]
			: []),

		{
			type: 'divider',
		},

		{
			key: 'custom-date',
			label: (
				<div
					style={{
						position: 'relative',
						overflow: 'hidden',
					}}
					onClick={(event) => {
						event.stopPropagation();
						setShowCustomRange(true);
					}}
				>
					<div>{t('date.custom-range')}</div>
					<div
						onClick={(event) => {
							event.stopPropagation();
						}}
					>
						<DatePicker.RangePicker
							open={showCustomRange}
							value={value}
							onChange={handleRangeChange}
							disabledDate={disabledDate()}
							maxDate={maxDate}
							className={styles.picker}
							classNames={{ popup: { root: styles.popup } }}
						/>
					</div>
				</div>
			),
		},
	];

	return (
		<Dropdown
			open={open}
			trigger={['click']}
			destroyOnHidden
			menu={{ items }}
			onOpenChange={(nextOpen) => {
				setOpen(nextOpen);

				if (!nextOpen) {
					setShowCustomRange(false);
				}
			}}
		>
			<Button block={fullWidth} className={styles.button}>
				<div>{value?.[0]?.format('DD.MM.YYYY') ?? t('date.period-start')}</div>
				<div>{' — '}</div>
				<div>{value?.[1]?.format('DD.MM.YYYY') ?? t('date.period-end')}</div>
			</Button>
		</Dropdown>
	);
};
